import { useEffect, useState } from "react";
import { Button, EmptyState, PageTitle, TextInput } from "src/components";
import { PageLoader } from "src/components/page-loader";
import Axios from "src/services/api";
import { Count } from "../Customers/Customers";
import { Link, useNavigate } from "react-router-dom";
import styles from "./BoughtProducts.module.scss";
import { downloadFile } from "../Sales/CheckoutSuccess/utils";
import dayjs from "dayjs";
import { formatCurrency } from "src/services/currency";

const columns = [
  "Actions",
  "Order ID",
  "Product or Subscription",
  "Status",
  "Paid",
  "Date purchased",
  "Next invoice",
  "License key",
];

const BoughtProducts: React.FC = () => {
  const navigate = useNavigate();
  const [boughtProducts, setBoughtProducts] = useState<any[]>([]);
  const [filteredBoughtProducts, setFilteredBoughtProducts] = useState<any[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    Axios.get("/bought-products").then((response) => {
      setBoughtProducts(response.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (search === "") {
      setFilteredBoughtProducts(boughtProducts);
    } else {
      setFilteredBoughtProducts(
        boughtProducts.filter((product) =>
          product.product.name.includes(search)
        )
      );
    }
  }, [search, boughtProducts]);

  if (loading) return <PageLoader />;

  return (
    <div className={styles.pageContainer}>
      <PageTitle
        actions={
          <div className={styles.searchInputContainer}>
            <div className={styles.seachInput}>
              <TextInput
                name="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products"
              />
            </div>
          </div>
        }
      >
        Purchased products
      </PageTitle>

      {boughtProducts.length === 0 ? (
        <EmptyState
          loading={loading}
          title="You haven't purchased any products"
          subTitle="If you purchase any products from other creators, you will see them here."
        />
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.productsTable}>
              <thead>
                <tr>
                  {columns.map((column, index) => (
                    <th key={index}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredBoughtProducts?.map((boughtProduct, index) => {
                  if (boughtProduct.sale) {
                    return (
                      <tr key={index}>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Link to={`/invoice/${boughtProduct.sale?._id}`}>
                              <Button text="Invoice" variant="secondary" />
                            </Link>
                            <Button
                              text="Get product"
                              onClick={() => {
                                if (boughtProduct.sale.emailCallbackUrl) {
                                  // window.open(product.url, "_blank");
                                  const a = document.createElement("a");
                                  a.href = boughtProduct.sale.emailCallbackUrl;
                                  a.rel = "noopener noreferrer";
                                  a.target = "_blank";
                                  a.click();
                                } else if (
                                  boughtProduct.product?.hasCallbackUrl
                                ) {
                                  window.location.href = `${boughtProduct.product?.callbackUrl}?productId=${boughtProduct.product?._id}&paymentIntentId=${boughtProduct.sale?.stripePaymentIntentId}&buyerEmail=${boughtProduct.sale?.buyerEmail}&orderId=${boughtProduct.sale?._id}&redirect_status=succeeded`;
                                } else if (boughtProduct.product?.isFile) {
                                  downloadFile(
                                    boughtProduct.product?.file,
                                    boughtProduct.product?.name
                                  );
                                } else {
                                  // window.open(product.url, "_blank");
                                  const a = document.createElement("a");
                                  a.href = boughtProduct.product?.url;
                                  a.rel = "noopener noreferrer";
                                  a.target = "_blank";
                                  a.click();
                                }
                              }}
                            />
                          </div>
                        </td>
                        <td
                          className={
                            boughtProduct.product ? "" : styles.deleted
                          }
                        >
                          <div className={styles.orderId}>
                            <div className={styles.ellipsis}>
                              #{boughtProduct.sale?._id!.toUpperCase()}
                            </div>
                            {boughtProduct.sale?.isRefunded! && (
                              <div className={styles.refunded}>Refunded</div>
                            )}
                          </div>
                        </td>
                        <td>
                          {boughtProduct.product ? (
                            <div className={styles.productNameContainer}>
                              <img
                                src={
                                  boughtProduct.product!?.thumbnail ||
                                  "https://dummyimage.com/1200&text=Product+Image"
                                }
                              />

                              <div className={styles.ellipsis}>
                                {boughtProduct.product!?.name || "Untitled"}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`${styles.productNameContainer} ${styles.deleted}`}
                            >
                              <img src="https://dummyimage.com/1200&text=Product+Image" />
                              <div className={styles.ellipsis}>Deleted</div>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className={`${styles.status} ${styles.active}`}>
                            Paid
                          </div>
                        </td>
                        <td style={{ width: "10%" }}>
                          {formatCurrency(
                            boughtProduct.sale?.amount! / 100,
                            boughtProduct.sale?.currency!?.toUpperCase() ||
                              "USD"
                          )}
                        </td>
                        <td>
                          <div>{dayjs(boughtProduct.createdAt).fromNow()}</div>
                        </td>
                        <td>—</td>
                        <td>
                          <div>{boughtProduct.licenseKey}</div>
                        </td>
                      </tr>
                    );
                  }

                  if (boughtProduct.subscriptionCustomer) {
                    const activePrice =
                      boughtProduct.stripeSubscription &&
                      boughtProduct.stripeSubscription?.items?.data.find(
                        (d: any) => d.plan.active === true
                      );
                    return (
                      <tr key={index}>
                        <td>
                          <Link
                            to={`https://localhost.pocketsflow.com/portal/${boughtProduct.subscription?._id}/${boughtProduct.subscriptionCustomer?._id}`}
                          >
                            <Button
                              text="Manage subscription"
                              variant="secondary"
                            />
                          </Link>
                        </td>
                        <td
                          className={
                            boughtProduct.product ? "" : styles.deleted
                          }
                        >
                          <div className={styles.orderId}>
                            <div className={styles.ellipsis}>
                              #
                              {boughtProduct.subscriptionCustomer?._id!.toUpperCase()}
                            </div>
                            {boughtProduct.subscriptionCustomer
                              ?.isRefunded! && (
                              <div className={styles.refunded}>Refunded</div>
                            )}
                          </div>
                        </td>
                        <td>
                          {boughtProduct.subscription ? (
                            <div className={styles.productNameContainer}>
                              <img
                                src={
                                  boughtProduct.subscription!?.thumbnail ||
                                  "https://dummyimage.com/1200&text=Product+Image"
                                }
                              />

                              <div className={styles.ellipsis}>
                                {boughtProduct.subscription?.name || "Untitled"}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`${styles.productNameContainer} ${styles.deleted}`}
                            >
                              <img src="https://dummyimage.com/1200&text=Product+Image" />
                              <div className={styles.ellipsis}>Deleted</div>
                            </div>
                          )}
                        </td>
                        <td>
                          <div
                            className={`${styles.status} ${
                              boughtProduct?.stripeSubscription! &&
                              boughtProduct?.stripeSubscription!.status ===
                                "active"
                                ? styles.active
                                : ""
                            }`}
                          >
                            {boughtProduct?.stripeSubscription! &&
                              boughtProduct?.stripeSubscription!.status}
                          </div>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(
                              activePrice?.price.unit_amount_decimal / 100,
                              activePrice?.price.currency.toUpperCase() || "USD"
                            )}{" "}
                            / {activePrice?.price.recurring.interval}
                          </div>
                        </td>
                        <td>
                          {dayjs(
                            boughtProduct?.subscriptionCustomer?.createdAt!
                          ).fromNow()}
                        </td>
                        <td>
                          {dayjs(
                            new Date(
                              boughtProduct.stripeSubscription! &&
                                boughtProduct.stripeSubscription!
                                  .current_period_end * 1000
                            )
                          ).fromNow()}
                        </td>
                        <td>—</td>
                      </tr>
                    );
                  }

                  return null;
                })}
              </tbody>
            </table>
          </div>
          <Count items={filteredBoughtProducts} />
        </>
      )}
    </div>
  );
};

export default BoughtProducts;
